import { FC, useId } from 'react';
import SelectVehicleBrand from './SelectVehicleBrand';
import SelectVehicleModel from './SelectVehicleModel';

interface CustomerVehicleModalProps {
  open: boolean;
  title: string;
  vehicleBrandId: string;
  vehicleModelId: string;
  vehicleRegistrationYear: string;
  vehicleRegistrationNumber: string;
  vehicleVin: string;
  vehicleChassisNumber: string;
  fuelType?: string[];
  disabled?: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onChange: (field: 'vehicleBrandId' | 'vehicleModelId' | 'vehicleRegistrationYear' | 'vehicleRegistrationNumber' | 'vehicleVin' | 'vehicleChassisNumber' | 'fuelType', value: string | string[]) => void;
  onCustomerChange?: (value: string) => void;
  formError?: string | null;
  customerId?: string;
  showCustomerField?: boolean;
}

const CustomerVehicleModal: FC<CustomerVehicleModalProps> = ({
  open,
  title,
  vehicleBrandId,
  vehicleModelId,
  vehicleRegistrationYear,
  vehicleRegistrationNumber,
  vehicleVin,
  vehicleChassisNumber,
  fuelType = [],
  disabled = false,
  onClose,
  onSubmit,
  onChange,
  formError,
}) => {
  const titleId = useId();

  const handleFuelTypeClick = (type: string) => {
    // Toggle logic: add if not present, remove if already selected
    const currentTypes = Array.isArray(fuelType) ? fuelType : [];
    const isSelected = currentTypes.includes(type);

    if (isSelected) {
      // Remove the type
      onChange('fuelType', currentTypes.filter(t => t !== type));
    } else {
      // Add the type
      onChange('fuelType', [...currentTypes, type]);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="flex w-full max-h-[90vh] max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl">
        <div className="flex w-full items-center border-b border-slate-200 bg-slate-50 px-6 py-4">
          <h2 id={titleId} className="flex-1 text-lg font-semibold text-slate-900">
            {title}
          </h2>
          <button
            type="button"
            className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/40"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
          noValidate
          className="space-y-4 overflow-y-auto px-6 py-6"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1 text-sm font-medium text-slate-700">
              <span>Brand</span>
              <SelectVehicleBrand
                value={vehicleBrandId}
                onChange={(brandId) => onChange('vehicleBrandId', brandId)}
                includePlaceholder
                placeholderLabel="Select brand"
                selectClassName="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                disabled={disabled}
              />
            </div>

            <div className="space-y-1 text-sm font-medium text-slate-700">
              <span>Model</span>
              <SelectVehicleModel
                brandId={vehicleBrandId}
                value={vehicleModelId}
                onChange={(modelId) => onChange('vehicleModelId', modelId)}
                includePlaceholder
                placeholderLabel="Select model"
                selectClassName="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                disabled={disabled || !vehicleBrandId}
              />
            </div>

            <label className="space-y-1 text-sm font-medium text-slate-700">
              Registration year
              <input
                type="number"
                min={1900}
                max={2999}
                value={vehicleRegistrationYear}
                onChange={(event) => onChange('vehicleRegistrationYear', event.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                disabled={disabled}
              />
            </label>

            <label className="space-y-1 text-sm font-medium text-slate-700">
              Registration number <span className="text-red-500">*</span>
              <input
                type="text"
                value={vehicleRegistrationNumber}
                onChange={(event) => onChange('vehicleRegistrationNumber', event.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                disabled={disabled}
                required
              />
            </label>
            <label className="space-y-1 text-sm font-medium text-slate-700">
              VIN
              <input
                type="text"
                value={vehicleVin}
                onChange={(event) => onChange('vehicleVin', event.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                disabled={disabled}
              />
            </label>

            <label className="space-y-1 text-sm font-medium text-slate-700">
              Chassis number
              <input
                type="text"
                value={vehicleChassisNumber}
                onChange={(event) => onChange('vehicleChassisNumber', event.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                disabled={disabled}
              />
            </label>

            <div className="sm:col-span-2 space-y-2">
              <label className="text-sm font-medium text-slate-700">Fuel type</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'petrol', label: 'PETROL' },
                  { value: 'diesel', label: 'DIESEL' },
                  { value: 'cng', label: 'CNG' },
                  { value: 'ev', label: 'EV' },
                  { value: 'petrol_hybrid', label: 'HYBRID' },
                ].map((fuel) => (
                  <button
                    key={fuel.value}
                    type="button"
                    onClick={() => handleFuelTypeClick(fuel.value)}
                    disabled={disabled}
                    className={`rounded-full px-5 py-2 text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-primary/30 ${(Array.isArray(fuelType) && fuelType.includes(fuel.value))
                      ? 'bg-primary text-white shadow-md hover:bg-primary/90'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300'
                      }`}
                  >
                    {fuel.label}
                  </button>
                ))}
              </div>
            </div>

          </div>

          {formError ? <p className="text-sm text-danger">{formError}</p> : null}

          <div className="md:col-span-2 mt-2 flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary/20"
              onClick={onClose}
              disabled={disabled}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/30"
              disabled={disabled}
            >
              {disabled ? 'Saving…' : 'Save vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerVehicleModal;
